import React, { useState, useEffect } from 'react';
import { X, Car, User, DollarSign, CreditCard, Calendar, FileText, AlertCircle, Search } from 'lucide-react';
import { vehicleService, customerService } from '../services/api';

export const SaleModal = ({ isOpen, onClose, onSubmit, editingSale = null, customers = [], vehicles = [] }) => {
  const [formData, setFormData] = useState({
    vehicle_id: '',
    customer_id: '',
    sale_price: '',
    down_payment: '',
    financing_amount: '',
    financing_term: '',
    monthly_payment: '',
    interest_rate: '',
    payment_method: 'cash',
    trade_in_vehicle: '',
    trade_in_value: '',
    sale_date: new Date().toISOString().split('T')[0],
    delivery_date: '',
    status: 'pending',
    commission: '',
    notes: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const paymentMethods = [
    { value: 'cash', label: 'À Vista' },
    { value: 'financing', label: 'Financiamento' },
    { value: 'trade_in', label: 'Troca' },
    { value: 'mixed', label: 'Misto' }
  ];

  const statusOptions = [
    { value: 'pending', label: 'Pendente' },
    { value: 'completed', label: 'Concluída' },
    { value: 'cancelled', label: 'Cancelada' },
    { value: 'delivered', label: 'Entregue' }
  ];

  // Preencher formulário quando estiver editando
  useEffect(() => {
    if (editingSale) {
      setFormData({
        vehicle_id: editingSale.vehicle_id || '',
        customer_id: editingSale.customer_id || '',
        sale_price: editingSale.sale_price || '',
        down_payment: editingSale.down_payment || '',
        financing_amount: editingSale.financing_amount || '',
        financing_term: editingSale.financing_term || '',
        monthly_payment: editingSale.monthly_payment || '',
        interest_rate: editingSale.interest_rate || '',
        payment_method: editingSale.payment_method || 'cash',
        trade_in_vehicle: editingSale.trade_in_vehicle || '',
        trade_in_value: editingSale.trade_in_value || '',
        sale_date: editingSale.sale_date || new Date().toISOString().split('T')[0],
        delivery_date: editingSale.delivery_date || '',
        status: editingSale.status || 'pending',
        commission: editingSale.commission || '',
        notes: editingSale.notes || ''
      });
    } else {
      // Resetar formulário para nova venda
      setFormData({
        vehicle_id: '',
        customer_id: '',
        sale_price: '',
        down_payment: '',
        financing_amount: '',
        financing_term: '',
        monthly_payment: '',
        interest_rate: '',
        payment_method: 'cash',
        trade_in_vehicle: '',
        trade_in_value: '',
        sale_date: new Date().toISOString().split('T')[0],
        delivery_date: '',
        status: 'pending',
        commission: '',
        notes: ''
      });
    }
    setErrors({});
    setSubmitError('');
  }, [editingSale, isOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Limpar erro do campo quando usuário começar a digitar
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    // Calcular valores automaticamente para financiamento usando os valores digitados como string
    if (name === 'sale_price' || name === 'down_payment') {
      const salePrice = name === 'sale_price' ? value : formData.sale_price;
      const downPayment = name === 'down_payment' ? value : formData.down_payment;

      const salePriceNumber = parseFloat(String(salePrice).replace(',', '.')) || 0;
      const downPaymentNumber = parseFloat(String(downPayment).replace(',', '.')) || 0;
      const financingAmount = salePriceNumber - downPaymentNumber;

      setFormData(prev => ({
        ...prev,
        financing_amount: financingAmount > 0 ? financingAmount.toString() : ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.vehicle_id) {
      newErrors.vehicle_id = 'Veículo é obrigatório';
    }

    if (!formData.customer_id) {
      newErrors.customer_id = 'Cliente é obrigatório';
    }

    if (!formData.sale_price || parseFloat(formData.sale_price) <= 0) {
      newErrors.sale_price = 'Preço de venda é obrigatório';
    }

    if (!formData.sale_date) {
      newErrors.sale_date = 'Data da venda é obrigatória';
    }

    if (formData.payment_method === 'financing' && (!formData.financing_term || parseInt(formData.financing_term) <= 0)) {
      newErrors.financing_term = 'Prazo do financiamento é obrigatório';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');
    
    try {
      // Preparar dados para envio
      const toNumber = (raw) => {
        if (raw === null || raw === undefined || raw === '') return null;
        const normalized = String(raw).replace(/\./g, '').replace(',', '.');
        const parsed = parseFloat(normalized);
        return Number.isNaN(parsed) ? null : parsed;
      };

      const saleData = {
        ...formData,
        sale_price: toNumber(formData.sale_price) ?? 0,
        down_payment: toNumber(formData.down_payment) ?? 0,
        financing_amount: toNumber(formData.financing_amount) ?? 0,
        financing_term: formData.financing_term ? parseInt(formData.financing_term, 10) : null,
        monthly_payment: toNumber(formData.monthly_payment),
        interest_rate: toNumber(formData.interest_rate),
        trade_in_value: toNumber(formData.trade_in_value),
        commission: toNumber(formData.commission),
        // Corrigir campos de data - enviar null se estiverem vazios
        sale_date: formData.sale_date || new Date().toISOString().split('T')[0],
        delivery_date: formData.delivery_date && formData.delivery_date.trim() !== '' ? formData.delivery_date : null,
        // Remover campos vazios que podem causar problemas
        trade_in_vehicle: formData.trade_in_vehicle && formData.trade_in_vehicle.trim() !== '' ? formData.trade_in_vehicle : null,
        notes: formData.notes && formData.notes.trim() !== '' ? formData.notes : null
      };

      await onSubmit(saleData);
      
      // Resetar formulário
      setFormData({
        vehicle_id: '',
        customer_id: '',
        sale_price: '',
        down_payment: '',
        financing_amount: '',
        financing_term: '',
        monthly_payment: '',
        interest_rate: '',
        payment_method: 'cash',
        trade_in_vehicle: '',
        trade_in_value: '',
        sale_date: new Date().toISOString().split('T')[0],
        delivery_date: '',
        status: 'pending',
        commission: '',
        notes: ''
      });
      setErrors({});
      setSubmitError('');
      onClose();
      
    } catch (error) {
      console.error('Erro ao cadastrar venda:', error);
      setSubmitError(error.message || 'Erro ao cadastrar venda. Tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      vehicle_id: '',
      customer_id: '',
      sale_price: '',
      down_payment: '',
      financing_amount: '',
      financing_term: '',
      monthly_payment: '',
      interest_rate: '',
      payment_method: 'cash',
      trade_in_vehicle: '',
      trade_in_value: '',
      sale_date: new Date().toISOString().split('T')[0],
      delivery_date: '',
      status: 'pending',
      commission: '',
      notes: ''
    });
    setErrors({});
    setSubmitError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-dark-700">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <DollarSign className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                {editingSale ? 'Editar Venda' : 'Registrar Venda'}
              </h2>
              <p className="text-gray-600 dark:text-gray-400">
                {editingSale ? 'Atualize os dados da venda' : 'Preencha os dados da venda'}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-lg transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {/* Erro de submit */}
          {submitError && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center space-x-3">
              <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
              <span className="text-red-700 dark:text-red-300 text-sm">{submitError}</span>
            </div>
          )}
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Coluna Esquerda */}
            <div className="space-y-6">
              {/* Veículo e Cliente */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
                  <Car className="h-5 w-5 text-primary" />
                  <span>Veículo e Cliente</span>
                </h3>
                <div className="space-y-4">
                  {/* Veículo */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Veículo *
                    </label>
                    <select
                      name="vehicle_id"
                      value={formData.vehicle_id}
                      onChange={handleInputChange}
                      className={`select ${errors.vehicle_id ? 'border-red-500' : ''}`}
                    >
                      <option value="">Selecione um veículo</option>
                      {vehicles.map(vehicle => (
                        <option key={vehicle.id} value={vehicle.id}>
                          {vehicle.brand} {vehicle.model} {vehicle.year} - {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(vehicle.price)}
                        </option>
                      ))}
                    </select>
                    {errors.vehicle_id && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.vehicle_id}</p>
                    )}
                  </div>

                  {/* Cliente */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Cliente *
                    </label>
                    <select
                      name="customer_id"
                      value={formData.customer_id}
                      onChange={handleInputChange}
                      className={`select ${errors.customer_id ? 'border-red-500' : ''}`}
                    >
                      <option value="">Selecione um cliente</option>
                      {customers.map(customer => (
                        <option key={customer.id} value={customer.id}>
                          {customer.name} - {customer.phone}
                        </option>
                      ))}
                    </select>
                    {errors.customer_id && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.customer_id}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Valores */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
                  <DollarSign className="h-5 w-5 text-primary" />
                  <span>Valores</span>
                </h3>
                <div className="space-y-4">
                  {/* Preço de Venda */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Preço de Venda *
                    </label>
                    <input
                      type="text"
                      name="sale_price"
                      value={formData.sale_price}
                      onChange={handleInputChange}
                      className={`input ${errors.sale_price ? 'border-red-500' : ''}`}
                      placeholder="150000 ou 150000.00"
                    />
                    {errors.sale_price && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.sale_price}</p>
                    )}
                  </div>

                  {/* Entrada */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Entrada
                    </label>
                    <input
                      type="text"
                      name="down_payment"
                      value={formData.down_payment}
                      onChange={handleInputChange}
                      className="input"
                      placeholder="Ex: 20000"
                    />
                  </div>

                  {/* Valor Financiado */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Valor Financiado
                    </label>
                    <input
                      type="text"
                      name="financing_amount"
                      value={formData.financing_amount}
                      className="input bg-gray-50 dark:bg-dark-700"
                      placeholder="Calculado automaticamente"
                      readOnly
                    />
                  </div>

                  {/* Comissão */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Comissão
                    </label>
                    <input
                      type="text"
                      name="commission"
                      value={formData.commission}
                      onChange={handleInputChange}
                      className="input"
                      placeholder="Ex: 1500"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Coluna Direita */}
            <div className="space-y-6">
              {/* Forma de Pagamento */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
                  <CreditCard className="h-5 w-5 text-primary" />
                  <span>Forma de Pagamento</span>
                </h3>
                <div className="space-y-4">
                  {/* Método de Pagamento */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Método de Pagamento
                    </label>
                    <select
                      name="payment_method"
                      value={formData.payment_method}
                      onChange={handleInputChange}
                      className="select"
                    >
                      {paymentMethods.map(method => (
                        <option key={method.value} value={method.value}>
                          {method.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Financiamento */}
                  {formData.payment_method === 'financing' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Prazo (meses) *
                        </label>
                        <input
                          type="number"
                          name="financing_term"
                          value={formData.financing_term}
                          onChange={handleInputChange}
                          className={`input ${errors.financing_term ? 'border-red-500' : ''}`}
                          placeholder="36"
                          min="1"
                          max="84"
                        />
                        {errors.financing_term && (
                          <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.financing_term}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Taxa de Juros (% a.a.)
                        </label>
                        <input
                          type="number"
                          name="interest_rate"
                          value={formData.interest_rate}
                          onChange={handleInputChange}
                          className="input"
                          placeholder="12.5"
                          step="0.1"
                          min="0"
                          max="50"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Parcela Mensal
                        </label>
                        <input
                          type="text"
                          name="monthly_payment"
                          value={formData.monthly_payment}
                          onChange={handleInputChange}
                          className="input"
                          placeholder="Ex: 1500"
                        />
                      </div>
                    </>
                  )}

                  {/* Troca */}
                  {formData.payment_method === 'trade_in' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Veículo de Troca
                        </label>
                        <input
                          type="text"
                          name="trade_in_vehicle"
                          value={formData.trade_in_vehicle}
                          onChange={handleInputChange}
                          className="input"
                          placeholder="Ex: Honda Civic 2018"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Valor da Troca
                        </label>
                        <input
                          type="text"
                          name="trade_in_value"
                          value={formData.trade_in_value}
                          onChange={handleInputChange}
                          className="input"
                          placeholder="Ex: 30000"
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Datas */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
                  <Calendar className="h-5 w-5 text-primary" />
                  <span>Datas</span>
                </h3>
                <div className="space-y-4">
                  {/* Data da Venda */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Data da Venda *
                    </label>
                    <input
                      type="date"
                      name="sale_date"
                      value={formData.sale_date}
                      onChange={handleInputChange}
                      className={`input ${errors.sale_date ? 'border-red-500' : ''}`}
                    />
                    {errors.sale_date && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.sale_date}</p>
                    )}
                  </div>

                  {/* Data de Entrega */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Data de Entrega
                    </label>
                    <input
                      type="date"
                      name="delivery_date"
                      value={formData.delivery_date}
                      onChange={handleInputChange}
                      className="input"
                    />
                  </div>

                  {/* Status */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Status
                    </label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      className="select"
                    >
                      {statusOptions.map(status => (
                        <option key={status.value} value={status.value}>
                          {status.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Observações */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
                  <FileText className="h-5 w-5 text-primary" />
                  <span>Observações</span>
                </h3>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Observações
                  </label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    rows="4"
                    className="input resize-none"
                    placeholder="Observações adicionais sobre a venda..."
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Botões */}
          <div className="flex justify-end space-x-4 mt-8 pt-6 border-t border-gray-200 dark:border-dark-700">
            <button
              type="button"
              onClick={handleClose}
              className="btn btn-outline"
              disabled={isSubmitting}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>{editingSale ? 'Atualizando...' : 'Registrando...'}</span>
                </div>
              ) : (
                editingSale ? 'Atualizar Venda' : 'Registrar Venda'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
